import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    role: '',
    publishingFocus: '',
    teamSize: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const roleOptions = [
    { value: 'pr-professional', label: 'PR Professional' },
    { value: 'marketing-manager', label: 'Marketing Manager' },
    { value: 'individual-author', label: 'Individual Author' },
    { value: 'publishing-executive', label: 'Publishing Executive' },
    { value: 'pr-coordinator', label: 'PR Coordinator' }
  ];

  const publishingFocusOptions = [
    { value: 'fiction', label: 'Fiction' },
    { value: 'non-fiction', label: 'Non-Fiction' },
    { value: 'academic', label: 'Academic Publishing' },
    { value: 'children', label: 'Children\'s Books' },
    { value: 'young-adult', label: 'Young Adult' },
    { value: 'business', label: 'Business & Professional' },
    { value: 'self-help', label: 'Self-Help & Personal Development' },
    { value: 'multiple', label: 'Multiple Genres' }
  ];

  const teamSizeOptions = [
    { value: 'individual', label: 'Individual (Just me)' },
    { value: '2-5', label: '2-5 people' },
    { value: '6-15', label: '6-15 people' },
    { value: '16-50', label: '16-50 people' },
    { value: '50+', label: '50+ people' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePassword = (password) => {
    const minLength = password?.length >= 8;
    const hasUpperCase = /[A-Z]/?.test(password);
    const hasLowerCase = /[a-z]/?.test(password);
    const hasNumbers = /\d/?.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/?.test(password);

    return {
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar
    };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const passwordValidation = validatePassword(formData?.password);
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordValidation?.isValid) {
      newErrors.password = 'Password does not meet requirements';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select your role';
    }

    if (!formData?.publishingFocus) {
      newErrors.publishingFocus = 'Please select your publishing focus';
    }

    if (!formData?.teamSize) {
      newErrors.teamSize = 'Please select your team size';
    }

    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock validation - check for existing email
      if (formData?.email === 'existing@example.com') {
        setErrors({ email: 'An account with this email already exists' });
        return;
      }

      // Success - redirect to email verification
      alert('Registration successful! Please check your email for verification.');
      
    } catch (error) {
      setErrors({ submit: 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const passwordValidation = validatePassword(formData?.password);

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Full Name */}
        <Input
          label="Full Name"
          type="text"
          name="fullName"
          placeholder="Enter your full name"
          value={formData?.fullName}
          onChange={handleInputChange}
          error={errors?.fullName}
          required
        />

        {/* Email */}
        <Input
          label="Email Address"
          type="email"
          name="email"
          placeholder="Enter your email address"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          description="We'll use this for account verification and important updates"
          required
        />

        {/* Password */}
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>

        {/* Password Strength Indicator */}
        {formData?.password && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Password Requirements:</p>
            <div className="space-y-1">
              <div className={`flex items-center space-x-2 text-xs ${passwordValidation?.minLength ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordValidation?.minLength ? "Check" : "X"} size={12} />
                <span>At least 8 characters</span>
              </div>
              <div className={`flex items-center space-x-2 text-xs ${passwordValidation?.hasUpperCase ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordValidation?.hasUpperCase ? "Check" : "X"} size={12} />
                <span>One uppercase letter</span>
              </div>
              <div className={`flex items-center space-x-2 text-xs ${passwordValidation?.hasLowerCase ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordValidation?.hasLowerCase ? "Check" : "X"} size={12} />
                <span>One lowercase letter</span>
              </div>
              <div className={`flex items-center space-x-2 text-xs ${passwordValidation?.hasNumbers ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordValidation?.hasNumbers ? "Check" : "X"} size={12} />
                <span>One number</span>
              </div>
              <div className={`flex items-center space-x-2 text-xs ${passwordValidation?.hasSpecialChar ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordValidation?.hasSpecialChar ? "Check" : "X"} size={12} />
                <span>One special character</span>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Password */}
        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            placeholder="Confirm your password"
            value={formData?.confirmPassword}
            onChange={handleInputChange}
            error={errors?.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
          >
            <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>

        {/* Company Name */}
        <Input
          label="Company Name"
          type="text"
          name="companyName"
          placeholder="Enter your company or organization name"
          value={formData?.companyName}
          onChange={handleInputChange}
          error={errors?.companyName}
          required
        />

        {/* Role Selection */}
        <Select
          label="Your Role"
          placeholder="Select your role"
          options={roleOptions}
          value={formData?.role}
          onChange={(value) => handleSelectChange('role', value)}
          error={errors?.role}
          required
        />

        {/* Publishing Focus */}
        <Select
          label="Publishing Focus"
          placeholder="Select your primary publishing focus"
          options={publishingFocusOptions}
          value={formData?.publishingFocus}
          onChange={(value) => handleSelectChange('publishingFocus', value)}
          error={errors?.publishingFocus}
          description="This helps us personalize your experience"
          required
        />

        {/* Team Size */}
        <Select
          label="Team Size"
          placeholder="Select your team size"
          options={teamSizeOptions}
          value={formData?.teamSize}
          onChange={(value) => handleSelectChange('teamSize', value)}
          error={errors?.teamSize}
          required
        />

        {/* Terms and Conditions */}
        <Checkbox
          label={
            <span className="text-sm">
              I agree to the{' '}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
            </span>
          }
          checked={formData?.acceptTerms}
          onChange={(e) => handleInputChange(e)}
          name="acceptTerms"
          error={errors?.acceptTerms}
          required
        />

        {/* Submit Error */}
        {errors?.submit && (
          <div className="p-3 bg-error/10 border border-error/20 rounded-md">
            <p className="text-sm text-error">{errors?.submit}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
        >
          Create Account
        </Button>

        {/* Login Link */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in here
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;